
import { registerLocaleData } from '@angular/common';
import localeEsAr from '@angular/common/locales/es-AR';
import localeEsGt from '@angular/common/locales/es-GT';
import localeEsBo from '@angular/common/locales/es-BO';
import localeEsCo from '@angular/common/locales/es-CO';
import localeEsPe from '@angular/common/locales/es-PE';
import localeEnCa from '@angular/common/locales/en-CA';
import { IOption, TimezoneResponse } from '../authentication/connection/interfaces/apiInterfaces';
import { Injectable, Injector } from '@angular/core';
import { IdentityService, RolesEnum } from '../authentication/identity.service';
import { HttpClient } from '@angular/common/http';
import { ConnectionServices } from '../authentication/connection/connection.service';
import { MultiRegionService } from '../multiregion/multiregion.service';
import { BehaviorSubject, catchError, concatMap, from, map, Observable, of, tap, throwError } from 'rxjs';
import { GooglePlacesService } from '../google-places.service';
import { ListTimezonesCommand } from './command/list-timezones.command';

registerLocaleData(localeEsAr);
registerLocaleData(localeEsGt);
registerLocaleData(localeEsBo);
registerLocaleData(localeEsCo);
registerLocaleData(localeEsPe);
registerLocaleData(localeEnCa);

export enum MeasureUnitsEnum {
  METRIC = 'METRIC',
  IMPERIAL = 'IMPERIAL',
}

export interface ILocaleData {
  currency: {
    name: string;
    name_plural: string;
    symbol: string;
    key: string;
    decimalPlaces;
    decimalSeparator: string;
    thousandSeparator: string;
  };
  culture: string;
  locale: string;
  dateFormat: string;
  timeFormat: string;
  timeFormatMoment: string,
  dateFormatCalendar?: string;
  measureUnits: MeasureUnitsEnum;
  timezoneOffset?: string;
  timezoneOffsetMinutes?: number;
  mercadoPagoRegion?: string;
  phoneCode?: string,
  taxLanguage: {
    taxCodes: IOption[];
    tax_code_name: string;
    personal_id_number: string;
    business_id_number: string;
    [key: string]: any;
  };
}

export const DEFAULT_LOCALE_VALUE = {
  currency: {
    name: 'Dollar',
    name_plural: 'Dollars',
    symbol: '$',
    key: 'USD',
    decimalPlaces: 2,
    decimalSeparator: '.',
    thousandSeparator: ','
  },
  culture: 'en',
  locale: 'en-US',
  dateFormat: 'MM/dd/yyyy',
  timeFormat: 'h:mm a',
  timeFormatMoment: "h:mm A",
  dateFormatCalendar: 'mm/dd/yy',
  mercadoPagoRegion: 'MLA',
  phoneCode: '+1',
  measureUnits: MeasureUnitsEnum.IMPERIAL,
  taxLanguage: {
    taxCodes: [
      {
        value: 'USA-C1',
        label: 'Cat 1',
      },
      {
        value: 'USA-C2',
        label: 'Cat 2',
      },
      {
        value: 'USA-C2',
        label: 'Cat 3',
      },
    ],
    tax_code_name: 'EIN',
    specific_tax_lang_key_EXAMPLE:
      'Specific language key example for taxary language',
    personal_id_number: 'SSN',
    business_id_number: 'EIN',
  },
};

@Injectable()
export class LocalizationService {
  public timezones$: TimezoneResponse[];
  private currencySymbolMap: Map<string, string> = new Map();
  private currencyKeyMap: Map<string, string> = new Map();

  constructor(
    private _identityService: IdentityService,
    private _httpClient: HttpClient,
    // private _notificationsService: NotificationsService,
    private _placesService: GooglePlacesService,
    private _connectionService: ConnectionServices,
    // private _bsLocaleService: BsLocaleService,
    private _injector: Injector,
    private _multiregionService: MultiRegionService
  ) {}

  // tslint:disable-next-line:member-ordering
  private _localeData: BehaviorSubject<ILocaleData> =
    new BehaviorSubject<ILocaleData>(DEFAULT_LOCALE_VALUE);

  public get localeData(): ILocaleData {
    return this._localeData.getValue();
  }

  public set localeData(value: ILocaleData) {
    this._localeData.next(value);
  }

  // tslint:disable-next-line:member-ordering
  public localeChanges: Observable<void> = this._localeData.pipe(
    map((val) => null)
  );

  public getCountryLocaleSettings(localeCode: string): Observable<ILocaleData> {
    localeCode =
      !localeCode || localeCode === 'null' || localeCode === 'undefined'
        ? 'us'
        : localeCode.toLowerCase();
    return this._httpClient
      .get(`assets/l10n/${localeCode}.json`)
      .pipe(
        catchError((err) => {
          return this._httpClient.get(`assets/l10n/us.json`);
        })
      )
      .pipe(
        tap((value) => {
          this.setupCalendarDateFormat(value);
        }),
        concatMap((value) => {
          return this.setUTCOffset();
        }),
        catchError((err) => {
          // this._notificationsService.showInternalErrorMessage(
          //   'toast.general.localization',
          //   'toast.shared.error_code.500'
          // );
          return of(null);
        })
      );
  }

  public getCountryCurrencySymbolAsync(localeCode: string): Observable<string> {
    if (!this.currencySymbolMap.get(localeCode)) {
      return this.getCountryLocaleObject(localeCode).pipe(
        map((res) => {
          this.currencySymbolMap.set(localeCode, res.currency.symbol);
          return res.currency.symbol;
        })
      );
    } else {
      return of(this.currencySymbolMap.get(localeCode));
    }
  }
  public getCountryCurrencySymbol(localeCode: string): string {
    if (!this.currencySymbolMap.get(localeCode)) {
      this.getCountryCurrencySymbolAsync(localeCode).subscribe();
      return '$';
    } else {
      return this.currencySymbolMap.get(localeCode);
    }
  }

  public getCountryCurrencyAsync(localeCode: string): Observable<string> {
    if (!this.currencyKeyMap.get(localeCode)) {
      return this.getCountryLocaleObject(localeCode).pipe(
        map((res) => {
          this.currencyKeyMap.set(localeCode, res.currency.key);
          return res.currency.key;
        })
      );
    } else {
      return of(this.currencyKeyMap.get(localeCode));
    }
  }

  public getCountryCurrency(localeCode: string): string {
    if (!this.currencyKeyMap.get(localeCode)) {
      this.getCountryCurrencyAsync(localeCode).subscribe();
      return 'USD';
    } else {
      return this.currencyKeyMap.get(localeCode);
    }
  }

  public getCountryLocaleObject(localeCode: string): Observable<ILocaleData> {
    localeCode =
      !localeCode || localeCode === 'null' || localeCode === 'undefined'
        ? 'us'
        : localeCode;
    return this._httpClient.get(`assets/l10n/${localeCode}.json`).pipe(
      map((v) => v as ILocaleData),
      catchError((err) => {
        return this._httpClient
          .get(`assets/l10n/us.json`)
          .pipe(map((v) => v as ILocaleData));
      })
    );
  }

  public setUTCOffset(): Observable<any> {
    let rol: string;
    try {
      rol = this._identityService.rol;
    } catch (e) {
      rol = null;
    }
    if (rol) {
      if (rol && rol !== RolesEnum.ROLE_ADMIN) {
        return this._placesService
          .getUTCOffsetByPlaceLatLng(
            this._identityService.loadLocationPlace().latitude,
            this._identityService.loadLocationPlace().longitude
          )
          .pipe(
            map((res) => {
              this.localeData.timezoneOffsetMinutes = res;
              this.localeData.timezoneOffset =
                this._placesService.getTimezoneOffset(res);
              // this._bsLocaleService.use(this.localeData.locale);
              return this.localeData;
            })
          );
      } else {
        return this.getTimeZones().pipe(
          concatMap((result) => {
            let tz = result.find(
              (value) => value.id === this._identityService.timezone
            );
            return tz
              ? this._placesService.getUTCOffsetByTimezoneName(tz.name).pipe(
                  map((res) => {
                    this.localeData.timezoneOffsetMinutes = res;
                    this.localeData.timezoneOffset =
                      this._placesService.getTimezoneOffset(res);
                    // this._bsLocaleService.use(this.localeData.locale);
                    return this.localeData;
                  })
                )
              : of(null);
          })
        );
      }
    } else {
      return from(this.setLatLngNoRol());
    }
  }

  async setLatLngNoRol(): Promise<any> {
    const current = await this._identityService.getLatLngCurrentPosition();
    return this._placesService
      .getUTCOffsetByPlaceLatLng(current.latitude, current.longitude)
      .pipe(
        map((res) => {
          this.localeData.timezoneOffsetMinutes = res;
          this.localeData.timezoneOffset =
            this._placesService.getTimezoneOffset(res);
          // this._bsLocaleService.use(this.localeData.locale);
          return this.localeData;
        })
      ).toPromise();  // Convertimos el observable a promesa para el manejo con await
  }

  public makeUTCDate(date: Date): Date {
    let actualOffset = this.localeData.timezoneOffsetMinutes * 60 * 1000;
    let browserOffset = new Date().getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() + actualOffset + browserOffset);
  }

  public makeUTCDateWithOffSet(date: Date, offSet: number): Date {
    let actualOffset = offSet * 60 * 1000;
    let browserOffset = new Date().getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() + actualOffset + browserOffset);
  }

  public makeLocalizedDate(date: Date): Date {
    let actualOffset = this.localeData.timezoneOffsetMinutes * 60 * 1000;
    let browserOffset = new Date().getTimezoneOffset() * 60 * 1000;
    return new Date(date.getTime() - actualOffset - browserOffset);
  }

  public getTimeZones(): Observable<TimezoneResponse[]> {
    if (this.timezones$) {
      return of(this.timezones$);
    }
    const listTimezonesCommand = new ListTimezonesCommand();

    listTimezonesCommand.setParameters();

    return this._connectionService
    .Request(listTimezonesCommand)
    .pipe(
      map((response) => {
        this.timezones$ = response;
        return response;
      }),
      catchError((error) => {
        // Muestra el error o lanza un error manejado
        // this._notificationsService.showHttpErrorMessage('localization', error);
        return throwError(() => new Error('Error en la petición de zonas horarias'));
      })
    );
  }

  /*  getLocalizedCurrency(value: string, countryCode: string): Observable<string> {
    let currencySymbolFormat: CurrencySymbolFormat = new CurrencySymbolFormat(this._multiregionService, this);
    return this.getCountryCurrencySymbol(countryCode.toLowerCase())
        .pipe(map(currencySymbol => {
          return currencySymbolFormat.transform(
              value,
              currencySymbol
          )
        }))
  }*/

  private setupCalendarDateFormat(value: Object) {
    this.localeData = value as ILocaleData;
    let tempData = this.localeData;
    if (this.localeData.locale == "en-CA") {
      tempData.dateFormatCalendar = 'yy/mm/dd';
    } else {
      if (this.localeData.dateFormat.substr(0, 2).toLowerCase() === 'mm') {
        tempData.dateFormatCalendar = 'mm/dd/yy';
      } else {
        tempData.dateFormatCalendar = 'dd/mm/yy';
      }
    }
    this.localeData = tempData;
  }

  public getLocaleData(code) {
    return this._httpClient.get(`assets/l10n/${code}.json`)
  }

  private getLocalStorageKey(code: string): string {
    const carrierId = this._identityService.userId;
    return `carrier-${code}${carrierId}.json`;
  }

  private async getCachedLocaleData(code: string): Promise<any> {
    const localStorageKey = this.getLocalStorageKey(code);
    const cachedData = localStorage.getItem(localStorageKey);

    if (cachedData) {
      return JSON.parse(cachedData);
    }
    return null;
  }

  public async getIsMetricFromLocaleData(): Promise<boolean> {
    const countryCode = this._identityService.countryCode;
    const cachedData = await this.getCachedLocaleData(countryCode.toLowerCase());

    if (cachedData) {
      return cachedData.measureUnits === MeasureUnitsEnum.METRIC.toString();
    }

    try {
      const data: ILocaleData = await this._httpClient.get<ILocaleData>(`assets/l10n/${countryCode.toLowerCase()}.json`).toPromise();
      const localStorageKey = this.getLocalStorageKey(countryCode.toLowerCase());
      localStorage.setItem(localStorageKey, JSON.stringify(data));
      return data.measureUnits === MeasureUnitsEnum.METRIC.toString();
    } catch (error) {
      throw error;
    }
  }

  public async getCurrencyFromLocaleData(): Promise<string> {
    const countryCode = this._identityService.countryCode;
    const cachedData = await this.getCachedLocaleData(countryCode.toLowerCase());

    if (cachedData) {
      return cachedData.currency.key;
    }

    try {
      const data: ILocaleData = await this._httpClient.get<ILocaleData>(`assets/l10n/${countryCode.toLowerCase()}.json`).toPromise();
      const localStorageKey = this.getLocalStorageKey(countryCode.toLowerCase());
      localStorage.setItem(localStorageKey, JSON.stringify(data));
      return data.currency.key;
    } catch (error) {
      throw error;
    }
  }

  public async getIsMetric(): Promise<boolean> {
    try {
      const isMetric = await this.getIsMetricFromLocaleData();
      return isMetric;
    } catch (error) {
      console.log('Error:', error);
      throw error;
    }
  }

  public async getCurrency() {
    try {
      const currency = await this.getCurrencyFromLocaleData();
      return currency;
    } catch (error) {
      console.log('Error:', error);
      throw error;
    }
  }

  public getCountry(): Observable<string> {
    return of(this.localeData.culture || 'us');
  }

  getFormatDateFlatpickrInput() {
    if (this.localeData.dateFormat == 'MM/dd/yyyy') {
      return 'm/d/Y';
    }
    return 'd/m/Y';
  }

  formatAmount(value: string | number): string {
    const num = Number(value);
    if (isNaN(num)) { return '0'; }

    const formatted = new Intl.NumberFormat(
      this.localeData.locale,
      {
        minimumFractionDigits: this.localeData.currency.decimalPlaces || 2,
        maximumFractionDigits: this.localeData.currency.decimalPlaces || 2,
        useGrouping: true
      }
    ).format(num);

    // Normalizar separadores de miles:
    // Reemplazamos espacios duros (NBSP) y espacios finos por el thousandSeparator definido en tu config
    const thousandSep = this.localeData.currency.thousandSeparator || ' ';

    return formatted
      .replace(/\u00A0/g, thousandSep)  // NBSP
      .replace(/\u202F/g, thousandSep); // NARROW NBSP
  }
}
